import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { RenderTierPref } from '../lib/device-tier';

/**
 * Per-device RENDER-QUALITY preference (perf cost-census, increment 6 follow-up) — the player's manual
 * override for the office render resolution tier:
 *   • `auto` (default) → pick by hardware (lib/device-tier.detectDeviceTier): ≤2× on capable machines,
 *      ≤1.5× on low-power ones. The right answer for almost everyone.
 *   • `full` → force the 2× ceiling (a low-power device whose owner wants the crispest picture and will
 *      accept the frame cost; or a machine we mis-detected as weak).
 *   • `lite` → force the 1.5× ceiling for the smoothest frames on a struggling machine, even if the
 *      auto-detector thinks it is capable.
 *
 * LOCAL & per-device only — never shared, never server-side, never a KPI (Constitution Art. 1: a comfort
 * knob, not surveillance). Persisted to localStorage like the audio + push-to-talk preferences, and
 * applied LIVE (no reload): the office swaps `renderer.resolution` the moment this changes.
 */
interface RenderSettingsState {
  /** The render-quality preference. */
  tier: RenderTierPref;
  /** Set the preference. */
  setTier: (tier: RenderTierPref) => void;
}

export const useRenderSettingsStore = create<RenderSettingsState>()(
  persist(
    (set) => ({
      tier: 'auto',
      setTier: (tier) => set({ tier }),
    }),
    {
      name: 'duck-duck-office-render',
      version: 1,
      partialize: (s) => ({ tier: s.tier }),
    },
  ),
);
